// SignUpWall.tsx — the free content cap at the "Sign-Up" step (act1-c04-l06).
// A free user can work up to and including that step; going further asks them to
// become a member and unlock the full programme. This is an amount cap, not a
// time limit — free accounts are never time-limited. Purchase happens on the
// web; the button opens checkout. Members never see it. Dismissable, so the app
// stays browsable behind it.
import { Btn, Display, Body } from '@/components/ui';
import { Icon } from '@/components/icons';
import { MembershipRefresh } from '@/components/MembershipRefresh';
import { PURCHASE_URL, SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { useMembershipRecheckOnResume } from '@/hooks/useMembershipRecheckOnResume';
import { openPaywall } from '@/services/paywallStore';

export function SignUpWall({ onRefresh, onClose }: { onRefresh: () => Promise<boolean>; onClose: () => void }) {
  useMembershipRecheckOnResume(true, onClose);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 115,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'calc(var(--safe-top) + 32px) 30px calc(var(--safe-bottom) + 26px)',
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'calc(var(--safe-top) + 12px)',
          right: 16,
          background: 'none',
          border: 'none',
          color: 'var(--ink-soft)',
          minHeight: 44,
          minWidth: 44,
          cursor: 'pointer',
        }}
      >
        <Icon name="chevDown" size={22} />
      </button>

      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#FBF1DE',
          background: 'linear-gradient(150deg, #E1A943, #9B6712)',
          marginBottom: 20,
        }}
      >
        <Icon name="sparkle" size={34} />
      </div>
      <Display parts={{ pre: 'The rest is ', em: 'waiting', post: ' for you.' }} size={26} lh={1.18} />
      <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.6 }}>
        {SHOW_PURCHASE
          ? 'You’ve reached the end of the free part of the programme. To carry on through ACT 1 and everything beyond, become a member on our website, then sign back in and all of your progress will be exactly where you left it.'
          : SHOW_IAP
          ? 'You’ve reached the end of the free part of the programme. To carry on through ACT 1 and everything beyond, become a member. All of your progress will be exactly where you left it.'
          : 'You’ve reached the end of the free part of the programme. The rest of ACT 1 and everything beyond are part of full membership, which is managed on the ReNurture website. Once you’re a member, sign in here and your progress will be exactly where you left it.'}
      </Body>
      <div style={{ marginTop: 24, width: '100%', maxWidth: 320 }}>
        {SHOW_IAP && (
          <Btn full size="lg" onClick={openPaywall}>
            See membership options
          </Btn>
        )}
        {SHOW_PURCHASE && PURCHASE_URL && (
          <Btn
            full
            size="lg"
            onClick={() => window.open(PURCHASE_URL, '_blank', 'noopener')}
            leading={<Icon name="external" size={18} />}
          >
            Unlock the full programme
          </Btn>
        )}
        <MembershipRefresh onRefresh={onRefresh} onUnlocked={onClose} />
      </div>
    </div>
  );
}
